/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/ban-ts-comment -- vendored zero-dependency WebGL component; the project's strict index-access / no-explicit-any rules don't apply to third-party shader code */
// @ts-nocheck
'use client';

/**
 * voice-orb.tsx — animated, voice-reactive gradient orb (standalone WebGL).
 * Original shader, zero dependencies. Drop into `components/ui/voice-orb.tsx`.
 *
 *   import { VoiceOrb } from "@/components/ui/voice-orb";
 *   <VoiceOrb state="talking" preset="advertisement" className="size-80" />
 *
 * Real audio (mic or TTS) drives it via a live 0..1 level ref (no re-renders):
 *   const level = useMicLevel(true);
 *   <VoiceOrb state="listening" levelRef={level} />
 */

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MutableRefObject,
  type RefObject,
} from 'react';

/* ------------------------------------------------------------------ presets */

export const PRESETS = {
  narration: ['#f3c99a', '#ef8f86', '#e58aa0', '#c9b3e0', '#e9d4e8'],
  conversational: ['#e8943f', '#9bae5e', '#5f7e3a', '#6f9a8f', '#a9c6dd'],
  socialmedia: ['#f3c79a', '#ef9a72', '#ec6a5a', '#e85048', '#f0b89a'],
  educational: ['#f5c06a', '#f0a05e', '#ee8e7e', '#cf9aa0', '#9fb2d4'],
  advertisement: ['#f0a07a', '#ee8194', '#d98fc0', '#b98fd0', '#cdb0e0'],
  characters: ['#f0c79a', '#c9a6c4', '#8f86cf', '#6a64c0', '#7d78cf'],
  aurora: ['#0e3b3b', '#1f7a6b', '#3fae8e', '#9fd6a6', '#dff2c0'],
  candy: ['#7a2bd8', '#b248e8', '#ff6fb0', '#ff9ec7', '#ffd3e6'],
  ocean: ['#8ce8d6', '#4bb8c9', '#3a7bd5', '#5a5fd0', '#8a7fd8'],
  dusk: ['#ffd08a', '#ff9e6a', '#f76e5a', '#e0517a', '#a24fa0'],
  forest: ['#d6e89a', '#9bc46a', '#4f9d5a', '#2f7d6a', '#3a6a8f'],
  lavender: ['#f3d9f0', '#e0b8e8', '#c9a0e0', '#a88fd8', '#8f9fe0'],
  ember: ['#ffe08a', '#ff9e57', '#f0603a', '#c02f3a', '#7a1f4a'],
  ice: ['#eaf6ff', '#c9e6f5', '#a9d0ea', '#9fb8e0', '#b0a8e0'],
  peach: ['#fff0d8', '#ffd9b0', '#f7b894', '#ef9a86', '#e08fa0'],
  neon: ['#a0ffe0', '#5ce0ff', '#6a8fff', '#c06aff', '#ff6ad5'],
  slate: ['#e8ecf2', '#c0c8d4', '#98a4b8', '#707e98', '#4f5a70'],
  rose: ['#ffe0ec', '#ffb0cc', '#f78fb0', '#e06a94', '#b04f7a'],
  gold: ['#fff2c8', '#ffd97a', '#f0b84a', '#d98f3a', '#a86a2f'],
  twilight: ['#f0c9a0', '#d98fa0', '#a06ac0', '#5f5fc0', '#3a4f9a'],
} as const;

export type PresetName = keyof typeof PRESETS;
export type OrbState = 'idle' | 'listening' | 'thinking' | 'talking';
export type ThinkingStyle = 'comet' | 'vortex' | 'breathing' | 'sonar' | 'searching';

const THINK_MODE: Record<ThinkingStyle, number> = {
  comet: 0,
  vortex: 1,
  breathing: 2,
  sonar: 3,
  searching: 4,
};

/* ------------------------------------------------------------------- props */

export interface VoiceOrbProps {
  /** Animation state. Default "idle". */
  state?: OrbState;
  /** Effect used while `state === "thinking"`. Default "breathing". */
  thinkingStyle?: ThinkingStyle;
  /** 5 hex colors. Overrides `preset`. */
  colors?: readonly string[];
  /** Built-in palette. Default "advertisement". */
  preset?: PresetName;
  /** Controlled amplitude 0..1. When set, overrides the synthetic envelope. */
  audioLevel?: number;
  /** Live amplitude ref 0..1 (read every frame, no re-renders). From `useMicLevel` etc. */
  levelRef?: RefObject<number> | MutableRefObject<number>;
  /** Size in px. Omit and size via `className` (e.g. "size-80"). */
  size?: number;
  /** CSS grain overlay opacity 0..1. Default 0.2. */
  grain?: number;
  /** Outer glow 0..0.7. Default 0.22. */
  glow?: number;
  paused?: boolean;
  className?: string;
  style?: CSSProperties;
}

/* ------------------------------------------------------------------ shaders */

const VERT = `attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }`;

const FRAG = `
precision highp float;
uniform vec2  uRes;
uniform float uTime;
uniform vec3  uC0,uC1,uC2,uC3,uC4;
uniform float uScale, uWarp, uSpeed, uGrain, uGlow, uEdge, uGradMix, uContrast, uSeed;
uniform float uLevel;
uniform float uThink;
uniform float uThinkMode;
uniform float uRot;

vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
vec2 mod289(vec2 x){return x-floor(x*(1.0/289.0))*289.0;}
vec3 permute(vec3 x){return mod289(((x*34.0)+1.0)*x);}
float snoise(vec2 v){
  const vec4 C=vec4(0.211324865405187,0.366025403784439,-0.577350269189626,0.024390243902439);
  vec2 i=floor(v+dot(v,C.yy));
  vec2 x0=v-i+dot(i,C.xx);
  vec2 i1=(x0.x>x0.y)?vec2(1.0,0.0):vec2(0.0,1.0);
  vec4 x12=x0.xyxy+C.xxzz; x12.xy-=i1;
  i=mod289(i);
  vec3 p=permute(permute(i.y+vec3(0.0,i1.y,1.0))+i.x+vec3(0.0,i1.x,1.0));
  vec3 m=max(0.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.0);
  m=m*m; m=m*m;
  vec3 x=2.0*fract(p*C.www)-1.0;
  vec3 h=abs(x)-0.5;
  vec3 ox=floor(x+0.5);
  vec3 a0=x-ox;
  m*=1.79284291400159-0.85373472095314*(a0*a0+h*h);
  vec3 g; g.x=a0.x*x0.x+h.x*x0.y; g.yz=a0.yz*x12.xz+h.yz*x12.yw;
  return 130.0*dot(m,g);
}
float hash(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453); }

vec3 ramp(float t){
  t=clamp(t,0.0,1.0);
  if(t<0.25) return mix(uC0,uC1,t/0.25);
  if(t<0.50) return mix(uC1,uC2,(t-0.25)/0.25);
  if(t<0.75) return mix(uC2,uC3,(t-0.50)/0.25);
  return mix(uC3,uC4,(t-0.75)/0.25);
}

void main(){
  vec2 uv=(gl_FragCoord.xy/uRes-0.5)*2.0;
  vec2 sp=uv/(0.82*(1.0+uLevel*0.09));
  float d=length(sp);
  float t=uTime*uSpeed + uSeed;

  float rot=uRot;
  mat2 R=mat2(cos(rot),-sin(rot),sin(rot),cos(rot));
  vec2 q=(sp*R)*uScale + uSeed*7.0;
  float wAmt=uWarp + uLevel*1.0;
  vec2 w=vec2( snoise(q*0.7+t*0.10),
               snoise(q*0.7+4.0-t*0.09) );
  vec2 qq=q + w*wAmt + vec2(t*0.12,-t*0.10);
  float n=(snoise(qq)*0.72 + snoise(qq*2.1+1.7)*0.28)*0.5+0.5;

  float grad=sp.y*0.5+0.5;
  float m=mix(n,grad,uGradMix);
  m=clamp((m-0.5)*uContrast+0.5,0.0,1.0);
  vec3 col=ramp(m);

  // near-flat: only a whisper of volume, no lit-sphere look
  float z=sqrt(max(0.0,1.0-d*d));
  vec3 nrm=vec3(sp,z);
  float dif=dot(nrm,normalize(vec3(-0.30,0.55,0.9)))*0.5+0.5;
  col*=mix(0.97,1.04,dif);
  col*=mix(1.0,0.90,smoothstep(0.70,1.0,d));
  col*=1.0+uLevel*0.16;

  // soft pearlescent sheen (flat, drifting)
  float ht=uTime*0.26;
  vec2 hp=sp*0.9 + uSeed*3.0;
  float s1=snoise(hp + vec2(ht*0.5, ht*0.30));
  float s2=snoise(hp*0.7 + vec2(-ht*0.4, ht*0.45) + 11.0);
  float hl=(s1*0.6 + s2*0.4)*0.5 + 0.5;
  vec3 sheenCol=vec3(0.90,0.93,1.0);
  float sheen=smoothstep(0.38,0.90,hl)*(0.70+0.40*uLevel);
  col=mix(col, sheenCol, clamp(sheen*0.62,0.0,0.74));
  float wisp=smoothstep(0.66,0.98,hl)*(0.45+0.45*uLevel);
  col=mix(col, mix(sheenCol,vec3(1.0),0.5), clamp(wisp*0.46,0.0,0.6));

  // soft rim glow
  float rim=smoothstep(0.60,0.95,d)*(0.04+0.40*uLevel);
  col=mix(col, vec3(1.0), rim*0.3);

  // slight sine ripples near the edge
  float ang=atan(sp.y,sp.x);
  float wob=0.040*sin(ang*2.0+uTime*1.5)
           +0.025*sin(ang*3.0-uTime*1.1)
           +0.018*sin(ang*5.0+uTime*0.7);
  float rr=(d-(0.82+wob))/0.06;
  float ring=exp(-rr*rr)*(0.07+0.5*uLevel);
  col=mix(col, vec3(1.0), clamp(ring*0.55,0.0,0.7));

  // thinking effects
  if(uThink>0.001){
    float ang2=atan(sp.y,sp.x);
    float tl=0.0;
    if(uThinkMode<0.5){
      float ca=uTime*0.7;
      vec2 cp=vec2(cos(ca),sin(ca))*0.62;
      float cd=length(sp-cp);
      tl=exp(-cd*cd/0.05)*0.95;
      float er=(d-0.62)/0.10;
      tl+=exp(-er*er)*max(0.0,cos(ang2-ca))*0.22;
    } else if(uThinkMode<1.5){
      float arms=0.5+0.5*sin(ang2*3.0 + d*6.0 - uTime*2.5);
      tl=smoothstep(0.62,1.0,arms)*(1.0-smoothstep(0.85,1.0,d))*0.45;
      float eg=(fract(-uTime*0.4)-d)/0.06;
      tl+=exp(-eg*eg)*0.30;
    } else if(uThinkMode<2.5){
      tl=exp(-d*d/0.62)*uLevel*1.1;
    } else if(uThinkMode<3.5){
      float s=0.0;
      for(int i=0;i<2;i++){ float ph=fract(uTime*0.40+float(i)*0.5);
        float ee=(d-ph)/0.06; s+=exp(-ee*ee)*(1.0-ph); }
      tl=s*0.55;
    } else {
      vec2 wp=vec2(snoise(vec2(uTime*0.30,1.3)), snoise(vec2(4.7,uTime*0.30)))*0.6;
      float wd=length(sp-wp);
      tl=exp(-wd*wd/0.12)*0.75;
    }
    col=mix(col, vec3(1.0), clamp(tl*uThink*0.55,0.0,0.6));
  }

  col+=(hash(gl_FragCoord.xy+floor(uTime*7.0))-0.5)*uGrain;

  float alpha=1.0-smoothstep(1.0-uEdge,1.0,d);
  float halo=(1.0-smoothstep(1.0,1.75,d))*uGlow*(1.0-alpha);
  vec3 haloCol=ramp(0.62);
  gl_FragColor=vec4(mix(haloCol,col,alpha), max(alpha,halo));
}`;

/* ------------------------------------------------------------------ helpers */

const hexRgb = (h: string): [number, number, number] => {
  const n = parseInt(h.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};
const clamp01 = (n: number) => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

function grainDataURL(size = 180): string {
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const ctx = cv.getContext('2d')!;
  const img = ctx.createImageData(size, size);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = Math.max(0, Math.min(255, 128 + (Math.random() - 0.5) * 100));
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
    img.data[i + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return cv.toDataURL('image/png');
}

// speech-like amplitude: irregular syllable bursts with pauses (used when no real audio is given)
const _f = (x: number) => x - Math.floor(x);
const _h = (n: number) => _f(Math.sin(n * 127.1) * 43758.5453);
const _vn = (x: number) => {
  const i = Math.floor(x),
    f = x - i,
    u = f * f * (3 - 2 * f);
  return _h(i) + (_h(i + 1) - _h(i)) * u;
};
function speechEnv(t: number) {
  const env = _vn(t * 5.5) * 0.8 + _vn(t * 12.0) * 0.2;
  const gate = clamp01((_vn(t * 0.8 + 13.0) - 0.35) / 0.25);
  return clamp01(env * gate * 1.4);
}

// adaptive loudness normalizer: maps RMS to 0..1 within the voice's own range (loud -> intense, quiet -> calm)
function makeNormalizer() {
  let peak = 0.06;
  return (rms: number) => {
    peak = Math.max(rms, peak * 0.9994 + 0.00002);
    const floor = 0.012;
    const n = clamp01((rms - floor) / Math.max(0.02, peak - floor));
    return clamp01(Math.pow(n, 1.3)); // expander widens loud/quiet contrast
  };
}

/* ------------------------------------------------------- css fallback (no WebGL) */

// Some environments expose no usable WebGL context at all (GPU blocklist,
// headless/automation browsers, hardened privacy settings). getContext then
// returns null on every try and the shader can never draw. Rather than sit on
// the flat brand circle, we animate a lightweight CSS approximation built from
// the same palette so the orb still reads as "alive". Keyframes are injected
// once and namespaced.
let _orbKeyframesInjected = false;
function ensureOrbKeyframes() {
  if (_orbKeyframesInjected || typeof document === 'undefined') return;
  _orbKeyframesInjected = true;
  const el = document.createElement('style');
  el.setAttribute('data-voice-orb-fallback', '');
  el.textContent =
    '@keyframes voiceorb-spin{to{transform:rotate(360deg)}}' +
    '@keyframes voiceorb-drift{0%{transform:translate(-8%,-5%) scale(1)}100%{transform:translate(8%,6%) scale(1.14)}}' +
    '@keyframes voiceorb-pulse{0%,100%{opacity:.5}50%{opacity:.85}}';
  document.head.appendChild(el);
}

function OrbCssFallback({ palette, reduce }: { palette: string[]; reduce: boolean }) {
  const [c0, c1, c2, c3, c4] = palette;
  const anim = (v: string) => (reduce ? undefined : v);
  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden rounded-full">
      <div
        className="absolute rounded-full"
        style={{
          inset: '-30%',
          background: `conic-gradient(from 0deg, ${c0}, ${c2}, ${c4}, ${c1}, ${c3}, ${c0})`,
          filter: 'blur(8px) saturate(1.05)',
          animation: anim('voiceorb-spin 9s linear infinite'),
        }}
      />
      <div
        className="absolute rounded-full"
        style={{
          inset: '6%',
          background: `radial-gradient(circle at 32% 30%, ${c1}, transparent 62%)`,
          mixBlendMode: 'screen',
          animation: anim('voiceorb-drift 6.5s ease-in-out infinite alternate'),
        }}
      />
      <div
        className="absolute rounded-full"
        style={{
          inset: '18%',
          background: `radial-gradient(circle at 68% 70%, ${c3}, transparent 60%)`,
          mixBlendMode: 'screen',
          animation: anim('voiceorb-drift 8s ease-in-out infinite alternate-reverse'),
        }}
      />
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle at 38% 32%, rgba(255,255,255,.55), transparent 46%)',
          animation: anim('voiceorb-pulse 4s ease-in-out infinite'),
        }}
      />
    </div>
  );
}

/* --------------------------------------------------------------- component */

export function VoiceOrb({
  state = 'idle',
  thinkingStyle = 'breathing',
  colors,
  preset = 'advertisement',
  audioLevel,
  levelRef,
  size,
  grain = 0.2,
  glow = 0.22,
  paused = false,
  className,
  style,
}: VoiceOrbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const grainRef = useRef<HTMLDivElement>(null);
  // Bumped to re-init WebGL after a context is lost/restored or refused (see below).
  const [glEpoch, setGlEpoch] = useState(0);
  const glRetries = useRef(0);
  // WebGL is "live" once a context is obtained and the program is bound. Until
  // then — or forever, in browsers with no usable WebGL — the CSS fallback shows.
  const [glReady, setGlReady] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  const cfg = useRef<any>({});
  cfg.current = {
    state,
    thinkMode: THINK_MODE[thinkingStyle] ?? 0,
    colors: (colors as string[]) || PRESETS[preset] || PRESETS.advertisement,
    audioLevel,
    levelRef,
    glow,
    paused,
  };

  useEffect(() => {
    if (grainRef.current) grainRef.current.style.backgroundImage = `url(${grainDataURL()})`;
    ensureOrbKeyframes();
    setReduceMotion(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current!;
    // WebGL contexts are capped per page (browsers ~8–16). Under pressure the
    // browser can refuse a new context, drop a live one, or even hand out a
    // context that is ALREADY lost (getContext succeeds but isContextLost() is
    // true and every call silently no-ops) — that last case is why orbs
    // "sometimes never load": nothing errors and `webglcontextrestored` never
    // fires for it. Recovery is a FRESH CANVAS: the <canvas> is keyed by
    // glEpoch, because a canvas whose context was lost keeps returning the
    // same dead context object forever.
    let retry: ReturnType<typeof setTimeout> | undefined;
    const reinit = () => {
      if (glRetries.current >= 8) return;
      glRetries.current += 1;
      clearTimeout(retry);
      // growing backoff — context pressure usually clears within a second or two
      retry = setTimeout(() => setGlEpoch((v) => v + 1), 300 * glRetries.current);
    };
    const onLost = (e: Event) => {
      e.preventDefault();
      setGlReady(false);
      reinit();
    };
    const onRestored = () => setGlEpoch((v) => v + 1);
    canvas.addEventListener('webglcontextlost', onLost as EventListener, false);
    canvas.addEventListener('webglcontextrestored', onRestored, false);
    const detach = () => {
      clearTimeout(retry);
      canvas.removeEventListener('webglcontextlost', onLost as EventListener);
      canvas.removeEventListener('webglcontextrestored', onRestored);
    };

    const gl = canvas.getContext('webgl', {
      premultipliedAlpha: false,
      antialias: true,
      alpha: true,
    });
    if (!gl || gl.isContextLost()) {
      setGlReady(false);
      reinit();
      return detach;
    }
    glRetries.current = 0;

    const compile = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        // Context loss (too many live WebGL contexts) fails compiles with a
        // null info log — nothing actionable, so only log real messages.
        const log = gl.getShaderInfoLog(s);
        if (log) console.error(log);
      }
      return s;
    };
    const prog = gl.createProgram()!;
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    gl.useProgram(prog);
    // A usable context is bound; hand the pixels over from the CSS fallback.
    setGlReady(true);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    const U: Record<string, WebGLUniformLocation | null> = {};
    [
      'uRes',
      'uTime',
      'uRot',
      'uC0',
      'uC1',
      'uC2',
      'uC3',
      'uC4',
      'uScale',
      'uWarp',
      'uSpeed',
      'uGrain',
      'uGlow',
      'uEdge',
      'uGradMix',
      'uContrast',
      'uSeed',
      'uLevel',
      'uThink',
      'uThinkMode',
    ].forEach((n) => (U[n] = gl.getUniformLocation(prog, n)));

    // fixed look constants (matches the tuned playground)
    gl.uniform1f(U.uScale, 1.0);
    gl.uniform1f(U.uWarp, 0.45);
    gl.uniform1f(U.uSpeed, 0.18);
    gl.uniform1f(U.uGrain, 0.03);
    gl.uniform1f(U.uEdge, 0.06);
    gl.uniform1f(U.uGradMix, 0.5);
    gl.uniform1f(U.uContrast, 0.95);
    const seed = Math.random() * 10;
    gl.uniform1f(U.uSeed, seed);

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    let raf = 0,
      tAcc = 0,
      rotPhase = 0,
      lvl = 0,
      thinkLvl = 0,
      last = performance.now();

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const s = Math.round(canvas.clientWidth * dpr);
      if (s && canvas.width !== s) {
        canvas.width = s;
        canvas.height = s;
      }
    };

    const frame = () => {
      const now = performance.now();
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      const c = cfg.current;
      const t = now / 1000;

      const live =
        c.levelRef && typeof c.levelRef.current === 'number' ? c.levelRef.current : undefined;
      let target: number;
      if (typeof live === 'number') target = clamp01(live);
      else if (typeof c.audioLevel === 'number') target = clamp01(c.audioLevel);
      else if (c.state === 'idle') target = 0.12 + 0.04 * Math.sin(t * 0.7);
      else if (c.state === 'listening') target = 0.22 + 0.5 * speechEnv(t * 0.85);
      else if (c.state === 'talking') target = 0.12 + 0.9 * speechEnv(t);
      else {
        // thinking
        if (c.thinkMode === 2) {
          const cyc = (t % 3.4) / 3.4;
          target = 0.12 + 0.3 * Math.pow(Math.sin(Math.PI * cyc), 2);
        } else target = 0.15 + 0.04 * Math.sin(t * 0.8);
      }
      const thinkTarget =
        c.state === 'thinking' && typeof live !== 'number' && typeof c.audioLevel !== 'number'
          ? 1
          : 0;

      if (!c.paused && !reduce) {
        const k = target > lvl ? 0.4 : 0.16;
        lvl += (target - lvl) * k;
        thinkLvl += (thinkTarget - thinkLvl) * 0.06;
        tAcc += dt * (1.0 + lvl * 2.0);
        rotPhase += dt * (0.12 + thinkLvl * 0.45);
      } else if (reduce) {
        lvl = target;
        thinkLvl = thinkTarget;
      }

      resize();
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(U.uRes, canvas.width, canvas.height);
      gl.uniform1f(U.uTime, tAcc);
      gl.uniform1f(U.uRot, rotPhase);
      const cols = c.colors.map(hexRgb);
      gl.uniform3fv(U.uC0, cols[0]);
      gl.uniform3fv(U.uC1, cols[1]);
      gl.uniform3fv(U.uC2, cols[2]);
      gl.uniform3fv(U.uC3, cols[3]);
      gl.uniform3fv(U.uC4, cols[4]);
      gl.uniform1f(U.uGlow, c.glow);
      gl.uniform1f(U.uLevel, lvl);
      gl.uniform1f(U.uThink, thinkLvl);
      gl.uniform1f(U.uThinkMode, c.thinkMode);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      detach();
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, [glEpoch]);

  const useInline = size != null || !className;
  const px = size ?? 320;
  const wrapStyle: CSSProperties = useInline ? { width: px, height: px, ...style } : (style ?? {});
  const palette = ((colors as string[]) || PRESETS[preset] || PRESETS.advertisement) as string[];

  return (
    <div className={['relative', className].filter(Boolean).join(' ')} style={wrapStyle}>
      {/* No usable WebGL context (yet, or ever) — animate a CSS stand-in from the
          same palette instead of a dead flat circle. Unmounts once the shader
          takes over, so a working WebGL path renders exactly as before. */}
      {!glReady && <OrbCssFallback palette={palette} reduce={reduceMotion} />}
      {/* keyed by epoch: a lost context can only be replaced with a NEW canvas */}
      <canvas key={glEpoch} ref={canvasRef} className="absolute inset-0 block h-full w-full" />
      <div
        ref={grainRef}
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full mix-blend-overlay"
        style={{
          backgroundRepeat: 'repeat',
          backgroundSize: '180px',
          imageRendering: 'pixelated',
          opacity: grain,
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------- audio level hooks */

/** Live mic loudness as a ref (0..1), normalized to the voice's own range. */
export function useMicLevel(active = true): MutableRefObject<number> {
  const level = useRef(0);
  useEffect(() => {
    if (!active || typeof navigator === 'undefined') return;
    let stop = false,
      raf = 0;
    let ctx: AudioContext | undefined, stream: MediaStream | undefined;
    const norm = makeNormalizer();
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        ctx = new AudioContext();
        await ctx.resume();
        const src = ctx.createMediaStreamSource(stream);
        const an = ctx.createAnalyser();
        an.fftSize = 1024;
        src.connect(an);
        const time = new Uint8Array(an.fftSize);
        const tick = () => {
          if (stop) return;
          an.getByteTimeDomainData(time);
          let sum = 0;
          for (let i = 0; i < time.length; i++) {
            const v = (time[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / time.length);
          level.current += (norm(rms) - level.current) * 0.35;
          raf = requestAnimationFrame(tick);
        };
        tick();
      } catch (e) {
        console.warn('[VoiceOrb] mic unavailable:', e);
      }
    })();
    return () => {
      stop = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
      ctx?.close();
    };
  }, [active]);
  return level;
}

/** A media element may only ever be connected to ONE MediaElementSourceNode,
 *  so the context+source live in a WeakMap keyed by element and survive
 *  re-mounts (StrictMode, HMR). The source stays wired to the destination for
 *  the element's lifetime; each hook instance only taps in an analyser. */
const mediaGraphs = new WeakMap<
  HTMLMediaElement,
  { ctx: AudioContext; src: MediaElementAudioSourceNode }
>();

/** Live loudness (0..1) from an <audio>/<video> element — for TTS / agent playback. */
export function useAudioElementLevel(
  mediaRef: RefObject<HTMLMediaElement>,
  active = true,
): MutableRefObject<number> {
  const level = useRef(0);
  useEffect(() => {
    const el = mediaRef.current;
    if (!active || !el) return;
    let stop = false,
      raf = 0;
    let graph = mediaGraphs.get(el);
    if (!graph) {
      const ctx = new AudioContext();
      const src = ctx.createMediaElementSource(el);
      src.connect(ctx.destination);
      graph = { ctx, src };
      mediaGraphs.set(el, graph);
    }
    const { ctx, src } = graph;
    const an = ctx.createAnalyser();
    an.fftSize = 1024;
    src.connect(an);
    const time = new Uint8Array(an.fftSize);
    const norm = makeNormalizer();
    const tick = () => {
      if (stop) return;
      an.getByteTimeDomainData(time);
      let sum = 0;
      for (let i = 0; i < time.length; i++) {
        const v = (time[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / time.length);
      level.current += (norm(rms) - level.current) * 0.35;
      raf = requestAnimationFrame(tick);
    };
    const onPlay = () => {
      ctx.resume();
      tick();
    };
    el.addEventListener('play', onPlay);
    return () => {
      stop = true;
      cancelAnimationFrame(raf);
      el.removeEventListener('play', onPlay);
      try {
        src.disconnect(an);
      } catch {
        /* already detached */
      }
    };
  }, [mediaRef, active]);
  return level;
}

export default VoiceOrb;
